import { huellaGirada, type Caja } from './giro';

/**
 * Las operaciones de un lienzo de diseño: alinear, repartir, agrupar y las
 * guías que aparecen al arrastrar.
 *
 * Todo trabaja sobre la **huella girada**, que es el lugar que el elemento
 * ocupa de verdad: alinear por la caja sin girar dejaría una línea vertical
 * pegada al borde equivocado.
 *
 * Son funciones puras y devuelven sólo la posición nueva de cada elemento, así
 * que el editor las aplica igual esté moviendo uno o veinte.
 */

export type Ubicable = { id: string; x: number; y: number; ancho: number; alto: number; giro?: number; grupo?: string | null };

export type Alineacion =
  | 'izquierda'
  | 'centroH'
  | 'derecha'
  | 'arriba'
  | 'centroV'
  | 'abajo';

/** Dónde queda cada elemento después de la operación. */
export type Movimiento = Record<string, { x: number; y: number }>;

/** La caja que envuelve a un conjunto de elementos, ya girados. */
export function cajaDe(elementos: readonly Ubicable[]): Caja | null {
  if (elementos.length === 0) return null;

  const huellas = elementos.map(huellaGirada);
  const x1 = Math.min(...huellas.map((h) => h.x));
  const y1 = Math.min(...huellas.map((h) => h.y));
  const x2 = Math.max(...huellas.map((h) => h.x + h.ancho));
  const y2 = Math.max(...huellas.map((h) => h.y + h.alto));
  return { x: x1, y: y1, ancho: x2 - x1, alto: y2 - y1 };
}

/**
 * Alinea los elementos contra una referencia.
 *
 * Con uno solo elegido, la referencia es la etiqueta —«centrar» quiere decir
 * centrarlo en la etiqueta—; con varios, es la caja que los envuelve, que es
 * lo que uno espera al emparejar un grupo.
 */
export function alinear(elementos: readonly Ubicable[], como: Alineacion, referencia: Caja): Movimiento {
  const movimiento: Movimiento = {};

  for (const e of elementos) {
    const h = huellaGirada(e);
    let dx = 0;
    let dy = 0;

    switch (como) {
      case 'izquierda':
        dx = referencia.x - h.x;
        break;
      case 'centroH':
        dx = referencia.x + referencia.ancho / 2 - (h.x + h.ancho / 2);
        break;
      case 'derecha':
        dx = referencia.x + referencia.ancho - (h.x + h.ancho);
        break;
      case 'arriba':
        dy = referencia.y - h.y;
        break;
      case 'centroV':
        dy = referencia.y + referencia.alto / 2 - (h.y + h.alto / 2);
        break;
      case 'abajo':
        dy = referencia.y + referencia.alto - (h.y + h.alto);
        break;
    }

    movimiento[e.id] = { x: e.x + dx, y: e.y + dy };
  }

  return movimiento;
}

/**
 * Reparte los elementos dejando el mismo espacio entre uno y otro. El primero
 * y el último se quedan donde están: son los que fijan el tramo.
 */
export function distribuir(elementos: readonly Ubicable[], eje: 'horizontal' | 'vertical'): Movimiento {
  if (elementos.length < 3) return {};

  const conHuella = elementos
    .map((e) => ({ e, h: huellaGirada(e) }))
    .sort((a, b) => (eje === 'horizontal' ? a.h.x - b.h.x : a.h.y - b.h.y));

  const primero = conHuella[0]!;
  const ultimo = conHuella[conHuella.length - 1]!;

  const inicio = eje === 'horizontal' ? primero.h.x : primero.h.y;
  const fin = eje === 'horizontal' ? ultimo.h.x + ultimo.h.ancho : ultimo.h.y + ultimo.h.alto;
  const ocupado = conHuella.reduce((a, { h }) => a + (eje === 'horizontal' ? h.ancho : h.alto), 0);

  // Si no entran, se los deja como están antes que amontonarlos.
  const hueco = (fin - inicio - ocupado) / (conHuella.length - 1);
  if (hueco < 0) return {};

  const movimiento: Movimiento = {};
  let cursor = inicio;

  for (const { e, h } of conHuella) {
    if (eje === 'horizontal') {
      movimiento[e.id] = { x: e.x + (cursor - h.x), y: e.y };
      cursor += h.ancho + hueco;
    } else {
      movimiento[e.id] = { x: e.x, y: e.y + (cursor - h.y) };
      cursor += h.alto + hueco;
    }
  }

  return movimiento;
}

// ------------------------------------------------------------------- grupos

/** El identificador de grupo que sigue, mirando los que ya hay. */
export function siguienteGrupo(elementos: readonly Ubicable[]): string {
  const usados = new Set(elementos.map((e) => e.grupo).filter(Boolean));
  for (let i = 1; ; i++) {
    const nombre = `g${i}`;
    if (!usados.has(nombre)) return nombre;
  }
}

/**
 * Amplía una selección con los compañeros de grupo de cada elegido: tocar uno
 * de un grupo es tocarlos a todos, que es para lo que se agrupa.
 */
export function conSusGrupos(elementos: readonly Ubicable[], seleccion: ReadonlySet<string>): Set<string> {
  const grupos = new Set(
    elementos.filter((e) => seleccion.has(e.id) && e.grupo).map((e) => e.grupo as string),
  );
  const completa = new Set(seleccion);
  for (const e of elementos) if (e.grupo && grupos.has(e.grupo)) completa.add(e.id);
  return completa;
}

// -------------------------------------------------------- guías al arrastrar

export type Guia = { eje: 'x' | 'y'; en: number };

/**
 * Ajusta la posición de lo que se está arrastrando a lo que ya está puesto.
 *
 * Es lo que hace que dos textos queden alineados sin tener que mirar los
 * números: cuando un borde o el centro de lo que se mueve pasa cerca del borde
 * o el centro de otro elemento —o de la etiqueta—, se acomoda solo y se marca
 * la línea por la que se enganchó.
 */
export function ajustarConGuias(
  caja: Caja,
  otros: readonly Caja[],
  etiqueta: Caja,
  tolerancia: number,
): { dx: number; dy: number; guias: Guia[] } {
  const candidatos = (c: Caja, eje: 'x' | 'y') =>
    eje === 'x' ? [c.x, c.x + c.ancho / 2, c.x + c.ancho] : [c.y, c.y + c.alto / 2, c.y + c.alto];

  const referencias = [etiqueta, ...otros];
  const guias: Guia[] = [];
  let dx = 0;
  let dy = 0;

  for (const eje of ['x', 'y'] as const) {
    const propios = candidatos(caja, eje);
    let mejor: { distancia: number; correccion: number; en: number } | null = null;

    for (const referencia of referencias) {
      for (const destino of candidatos(referencia, eje)) {
        for (const propio of propios) {
          const distancia = Math.abs(destino - propio);
          if (distancia > tolerancia) continue;
          if (!mejor || distancia < mejor.distancia) {
            mejor = { distancia, correccion: destino - propio, en: destino };
          }
        }
      }
    }

    if (mejor) {
      if (eje === 'x') dx = mejor.correccion;
      else dy = mejor.correccion;
      guias.push({ eje, en: mejor.en });
    }
  }

  return { dx, dy, guias };
}
